import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Spin, Empty, message } from 'antd';
import { getSearches } from '../../services/api';
import { Search } from '../../types';
import SearchPanel from './components/SearchPanel';
import SearchPack from './components/SearchPack';
import SearchProgressDrawer from '../../components/drawers/SearchProgressDrawer';
import { groupSearches } from './groupSearches';
import { useSearchDrawer } from '../../contexts/SearchDrawerContext';
import { useNotifications } from '../../contexts/NotificationContext';
import { getApiErrorMessage } from '../../utils/errorUtils';
import css from './Searches.module.scss';
import SearchStartedNotice from './SearchStartedNotice';
import { shouldShowSearchStartedNotice } from './searchStartedNotice';

const Searches: React.FC = () => {
  const [searches, setSearches] = useState<Search[]>([]);
  const [loading, setLoading] = useState(true);
  const [progressSearchId, setProgressSearchId] = useState<string | null>(null);
  const [noticeCount, setNoticeCount] = useState(0);
  const { openDrawer, isOpen, drawerClosing, searchCreatedKey, createdSearchIds,
    consumeSearchCreated, refreshQuota } = useSearchDrawer();
  const { subscribe } = useNotifications();
  const { id: highlightSearchId } = useParams<{ id?: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // Deep-link from the tailoring editor: /searches?drawer=open re-opens the
  // creation drawer (its stored handoff draft restores the flow) and consumes
  // the param (replace) so refresh/back can't re-trigger it.
  useEffect(() => {
    if (searchParams.get('drawer') === 'open') {
      openDrawer();
      const next = new URLSearchParams(searchParams);
      next.delete('drawer');
      setSearchParams(next, { replace: true });
    }
  }, [searchParams]); // eslint-disable-line react-hooks/exhaustive-deps -- openDrawer/setSearchParams are stable; listing them adds noise, not correctness
  const highlightedIdRef = useRef<string | null>(null);
  // Gate the full-page spinner to the first load only. The SSE subscription
  // re-runs fetchSearches on every event; without this guard each event would
  // flip `loading` true and blank the page (unmounting the open progress drawer)
  // before re-mounting it. After the first load we update the list in place.
  const hasLoadedRef = useRef(false);

  const fetchSearches = async () => {
    if (!hasLoadedRef.current) setLoading(true);
    try {
      const response = await getSearches({ sort_field: 'created_at', sort_method: 'desc' });
      setSearches(response.searches);
      // Keep the "New search" gate in sync after loads/deletes (delete frees a slot).
      refreshQuota();
    } catch (error) {
      console.error('Fetch searches error:', error);
      message.error(getApiErrorMessage(error));
    } finally {
      hasLoadedRef.current = true;
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSearches();
    // `fetchSearches` is redefined every render, so listing it would refetch on
    // every render — including the ones the progress drawer causes while a
    // crawl streams. It is safe to leave out: everything it closes over is
    // either a useState setter, a module import, or `refreshQuota`, which
    // SearchDrawerContext defines as a `useCallback(..., [])` and therefore
    // never goes stale. The rule flags it only because it cannot see through
    // the `useSearchDrawer()` destructuring to prove that.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchCreatedKey]);

  // Effects run after the cards commit. Wait for the full batch and the
  // creation drawer's closing animation before covering the rendered list.
  useEffect(() => {
    if (loading || isOpen || drawerClosing || !createdSearchIds) return;
    const show = shouldShowSearchStartedNotice(searches, createdSearchIds);
    if (show === null) return;
    if (show) setNoticeCount(createdSearchIds.length);
    consumeSearchCreated();
  }, [loading, isOpen, drawerClosing, searches, createdSearchIds, consumeSearchCreated]);

  useEffect(() => {
    return subscribe(
      ['search_status_changed', 'crawl_progress', 'opportunities_ready'],
      () => { fetchSearches(); }
    );
    // Same `fetchSearches` omission as above, and here it is load-bearing:
    // listing it would tear down and re-open this SSE subscription on every
    // render, and every event this subscription receives causes a render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subscribe]);

  // Deep-link: scroll to and briefly highlight the targeted search panel.
  // Use a ref to fire the pulse only on the first render after the id appears,
  // not on every background refresh of the list.
  useEffect(() => {
    if (!highlightSearchId) {
      highlightedIdRef.current = null;
      return;
    }
    if (loading) return;
    if (highlightedIdRef.current === highlightSearchId) return;
    const exists = searches.some(s => s.id === highlightSearchId);
    if (!exists) {
      message.error('Search not found');
      navigate('/searches', { replace: true });
      return;
    }
    const el = document.querySelector<HTMLElement>(`[data-search-id="${highlightSearchId}"]`);
    if (!el) return;
    highlightedIdRef.current = highlightSearchId;
    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    el.classList.add('search-panel-highlight');
    const t = window.setTimeout(() => {
      el.classList.remove('search-panel-highlight');
    }, 2200);
    return () => {
      window.clearTimeout(t);
      el.classList.remove('search-panel-highlight');
    };
    // `navigate` is read in the not-found branch but not listed. react-router
    // rebuilds it whenever the location changes, so listing it would run this
    // effect's cleanup on any navigation — cancelling the 2.2s pulse and
    // stripping the highlight class early. The pulse is fired at most once per
    // id by `highlightedIdRef`, so re-running buys nothing either.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [highlightSearchId, loading, searches]);

  // Track the open search by id and re-derive the live object from the list each
  // render: the drawer stays correct when the list re-fetches on SSE events, and
  // open={!!progressSearch} auto-closes it if the search is deleted. Derived
  // above the early returns so the auto-close effect below stays unconditional.
  const progressSearch = progressSearchId
    ? searches.find((s) => s.id === progressSearchId) ?? null
    : null;

  // Once the open search vanishes from the list, drop the stale id so we don't
  // re-open it if a search with that id ever reappears.
  useEffect(() => {
    if (progressSearchId && !progressSearch) setProgressSearchId(null);
  }, [progressSearchId, progressSearch]);

  if (loading) {
    return (
      <div className={css.loadingContainer}>
        <Spin size="large" />
      </div>
    );
  }

  if (searches.length === 0) {
    return (
      <div className={css.emptyContainer}>
        <Empty
          description="No searches yet"
          image={Empty.PRESENTED_IMAGE_SIMPLE}
        >
          <p>Create your first job search to get AI-matched opportunities.</p>
          <button className={css.createButton} onClick={openDrawer}>
            Create New Search
          </button>
        </Empty>
      </div>
    );
  }

  const rows = groupSearches(searches);

  return (
    <>
      <div className={css.searches}>
        {rows.map((row) =>
          row.kind === 'pack' ? (
            <SearchPack
              key={`pack-${row.groupId}`}
              searches={row.searches}
              onUpdate={fetchSearches}
              onOpenProgress={(s) => setProgressSearchId(s.id)}
            />
          ) : (
            <SearchPanel
              key={row.search.id}
              search={row.search}
              onUpdate={fetchSearches}
              onOpenProgress={(s) => setProgressSearchId(s.id)}
            />
          )
        )}
      </div>
      <SearchProgressDrawer
        open={!!progressSearch}
        search={progressSearch}
        onClose={() => setProgressSearchId(null)}
      />
      <SearchStartedNotice count={noticeCount} onAcknowledge={() => setNoticeCount(0)} />
    </>
  );
};

export default Searches;
